import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { TakeittoLogo } from '../components/TakeittoLogo';
import { DriverDetailsModal } from './DriverDetailsModal';
import { 
  Building2, Users, Truck, Package, RefreshCw, LogOut, 
  Search, CheckCircle, XCircle, Eye, BarChart3, Clock,
  DollarSign, MapPin, Phone, Mail, Calendar, Activity,
  AlertCircle, Navigation, Link, Weight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Order Status Badge
const OrderStatusBadge = ({ status }) => {
  const config = {
    pending: { label: 'Pending', className: 'bg-amber-100 text-amber-700' },
    assigned: { label: 'Assigned', className: 'bg-blue-100 text-blue-700' },
    in_transit: { label: 'In Transit', className: 'bg-indigo-100 text-indigo-700' },
    delivered: { label: 'Delivered', className: 'bg-emerald-100 text-emerald-700' },
    delayed: { label: 'Delayed', className: 'bg-red-100 text-red-700' }
  };
  const { label, className } = config[status] || config.pending;
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${className}`}>{label}</span>;
};

// Status indicator
const StatusIndicator = ({ active }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
    active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
  }`}>
    {active ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
    {active ? 'Active' : 'Inactive'}
  </span>
);

// Driver approval status badge
const ApprovalBadge = ({ status }) => {
  const config = {
    pending: { label: 'Pending Approval', className: 'bg-amber-100 text-amber-700' },
    approved: { label: 'Approved', className: 'bg-emerald-100 text-emerald-700' },
    rejected: { label: 'Rejected', className: 'bg-red-100 text-red-700' },
  };
  const { label, className } = config[status] || config.approved;
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${className}`}>{label}</span>;
};

// Stat Card
const StatCard = ({ icon: Icon, label, value, color = 'indigo' }) => (
  <div className="bg-white rounded-xl p-3 sm:p-5 border">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg bg-${color}-100 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${color}-600`} />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-sm text-gray-500">{label}</p>
      </div>
    </div>
  </div>
);

export default function AdminManagement() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  
  const [merchants, setMerchants] = useState([]);
  const [users, setUsers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [merchantDetails, setMerchantDetails] = useState(null);
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [selectedDriver, setSelectedDriver] = useState(null);
  
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [merchantsRes, usersRes, driversRes, statsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/admin/merchants`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/personnel`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/orders`, { headers })
      ]);
      
      setMerchants(merchantsRes.data);
      setUsers(usersRes.data);
      setDrivers(driversRes.data);
      setStats(statsRes.data);
      setOrders(ordersRes.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        logout();
        navigate('/login');
      }
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, [token, logout, navigate]);

  const fetchMerchantDetails = async (merchantId) => {
    try {
      const res = await axios.get(`${API}/admin/merchants/${merchantId}`, { headers });
      setMerchantDetails(res.data);
      setSelectedMerchant(merchantId);
    } catch (error) {
      toast.error('Failed to load merchant details');
    }
  };

  const toggleMerchantStatus = async (merchantId, currentStatus) => {
    try {
      await axios.patch(`${API}/admin/merchants/${merchantId}`, { is_active: !currentStatus }, { headers });
      toast.success(`Merchant ${currentStatus ? 'deactivated' : 'activated'}`);
      fetchData();
      if (selectedMerchant === merchantId) {
        fetchMerchantDetails(merchantId);
      }
    } catch (error) {
      toast.error('Failed to update merchant');
    }
  };

  const approveDriver = async (driverId) => {
    try {
      await axios.post(`${API}/admin/drivers/${driverId}/approve`, {}, { headers });
      toast.success('Driver approved');
      setSelectedDriver(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to approve driver');
    }
  };

  const rejectDriver = async (driverId) => {
    const reason = window.prompt('Reason for rejection (optional):', '');
    if (reason === null) return;
    try {
      await axios.post(`${API}/admin/drivers/${driverId}/reject`, { reason: reason || undefined }, { headers });
      toast.success('Driver rejected');
      setSelectedDriver(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to reject driver');
    }
  };

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds (same as Dispatch Dashboard)
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const filteredMerchants = merchants.filter(m => 
    m.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredUsers = users.filter(u => 
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredDrivers = drivers.filter(d => 
    d.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredOrders = orders.filter(order => {
    // Status filter
    if (orderStatusFilter !== 'all' && order.status !== orderStatusFilter) return false;
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        order.customer_name?.toLowerCase().includes(term) ||
        order.delivery_address?.toLowerCase().includes(term) ||
        order.id?.toLowerCase().includes(term)
      );
    }
    return true;
  });

  // Order stats
  const orderStats = {
    total: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    assigned: orders.filter(o => o.status === 'assigned').length,
    in_transit: orders.filter(o => o.status === 'in_transit').length,
    delivered: orders.filter(o => o.status === 'delivered').length
  };

  const copyTrackingLink = (orderId) => {
    const trackingCode = orderId.slice(0, 8);
    const url = `${window.location.origin}/track/${trackingCode}`;
    navigator.clipboard.writeText(url);
    toast.success('Tracking link copied!');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100" data-testid="admin-management">
      {/* Header */}
      <header className="bg-white border-b px-3 sm:px-4 py-2 sm:py-3 flex flex-wrap items-center justify-between gap-2 sticky top-0 z-50">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <TakeittoLogo size="sm" />
          <div className="h-6 w-px bg-gray-200 hidden sm:block" />
          <span className="font-semibold text-gray-900 truncate">Admin Console</span>
        </div>
        
        <div className="flex items-center gap-2 sm:gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} title="Dispatch Dashboard">
            <MapPin className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Dispatch Dashboard</span>
          </Button>
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center">
              <span className="text-white font-semibold text-sm">
                {user?.name?.charAt(0) || 'A'}
              </span>
            </div>
            <div className="hidden sm:block">
              <p className="text-sm font-medium text-gray-900">{user?.name || 'Admin'}</p>
              <p className="text-xs text-gray-500">Administrator</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/login'); }}>
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <div className="bg-white rounded-xl p-3 sm:p-5 border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.total_merchants || 0}</p>
                <p className="text-sm text-gray-500">Total Merchants</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-3 sm:p-5 border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.total_users || 0}</p>
                <p className="text-sm text-gray-500">Total Users</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-3 sm:p-5 border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Truck className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.total_drivers || 0}</p>
                <p className="text-sm text-gray-500">Total Drivers</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-3 sm:p-5 border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Package className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.total_orders || 0}</p>
                <p className="text-sm text-gray-500">Total Orders</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search merchants, users, drivers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        
        {/* Tabs */}
        <Tabs defaultValue="orders">
          <TabsList className="mb-4 max-w-full overflow-x-auto justify-start">
            <TabsTrigger value="orders" className="gap-2">
              <Package className="w-4 h-4" />
              Orders ({orders.length})
            </TabsTrigger>
            <TabsTrigger value="merchants" className="gap-2">
              <Building2 className="w-4 h-4" />
              Merchants ({merchants.length})
            </TabsTrigger>
            <TabsTrigger value="users" className="gap-2">
              <Users className="w-4 h-4" />
              Users ({users.length})
            </TabsTrigger>
            <TabsTrigger value="drivers" className="gap-2">
              <Truck className="w-4 h-4" />
              Drivers ({drivers.length})
            </TabsTrigger>
          </TabsList>
          
          {/* Orders Tab */}
          <TabsContent value="orders">
            <div className="bg-white rounded-xl border">
              {/* Orders Header with Filters */}
              <div className="p-3 sm:p-4 border-b flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-4">
                  <h3 className="font-semibold text-gray-900 whitespace-nowrap">All Orders</h3>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span className="flex items-center gap-1"><AlertCircle className="w-3 h-3 text-amber-500" />{orderStats.pending}</span>
                    <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-blue-500" />{orderStats.assigned}</span>
                    <span className="flex items-center gap-1"><Navigation className="w-3 h-3 text-indigo-500" />{orderStats.in_transit}</span>
                    <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-500" />{orderStats.delivered}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={orderStatusFilter} onValueChange={setOrderStatusFilter}>
                    <SelectTrigger className="w-[140px] h-8 text-sm">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="assigned">Assigned</SelectItem>
                      <SelectItem value="in_transit">In Transit</SelectItem>
                      <SelectItem value="delivered">Delivered</SelectItem>
                      <SelectItem value="delayed">Delayed</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={fetchData}>
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {/* Orders Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Address</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Weight</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredOrders.map(order => {
                      const assignedDriver = drivers.find(d => d.id === order.assigned_driver_id);
                      return (
                        <tr key={order.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <span className="font-mono text-sm text-indigo-600">{order.id.slice(0, 8).toUpperCase()}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-medium text-gray-900">{order.customer_name}</span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-start gap-1 text-sm text-gray-600 max-w-xs">
                              <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0 text-gray-400" />
                              <span className="truncate">{order.delivery_address}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="flex items-center gap-1 text-sm text-gray-700">
                              <DollarSign className="w-3 h-3 text-gray-400" />
                              {order.order_value?.toFixed(2)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="flex items-center gap-1 text-sm text-gray-700">
                              <Weight className="w-3 h-3 text-gray-400" />
                              {order.total_weight}kg
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <OrderStatusBadge status={order.status} />
                          </td>
                          <td className="px-4 py-3">
                            {assignedDriver ? (
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                                  <Truck className="w-3 h-3 text-blue-600" />
                                </div>
                                <span className="text-sm text-gray-700">{assignedDriver.name}</span>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => copyTrackingLink(order.id)}
                              className="h-7 px-2"
                            >
                              <Link className="w-3 h-3 mr-1" />
                              Track
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                
                {filteredOrders.length === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No orders found</p>
                  </div>
                )}
              </div>
              
              {/* Orders Footer */}
              <div className="p-3 border-t bg-gray-50 text-sm text-gray-500">
                Showing {filteredOrders.length} of {orders.length} orders • Auto-refreshing every 30s
              </div>
            </div>
          </TabsContent>
          
          {/* Merchants Tab */}
          <TabsContent value="merchants">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Merchants List */}
              <div className="lg:col-span-2 bg-white rounded-xl border">
                <div className="p-4 border-b">
                  <h3 className="font-semibold text-gray-900">Registered Merchants</h3>
                </div>
                
                <div className="divide-y max-h-[600px] overflow-y-auto">
                  {filteredMerchants.map(merchant => (
                    <div 
                      key={merchant.id} 
                      className={`p-4 hover:bg-gray-50 cursor-pointer ${
                        selectedMerchant === merchant.id ? 'bg-indigo-50' : ''
                      }`}
                      onClick={() => fetchMerchantDetails(merchant.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">{merchant.business_name}</h4>
                            <p className="text-sm text-gray-500">{merchant.city}, {merchant.state}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="text-sm font-medium text-gray-900">{merchant.order_count} orders</p>
                            <p className="text-xs text-gray-500 capitalize">{merchant.plan} plan</p>
                          </div>
                          <StatusIndicator active={merchant.is_active} />
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {filteredMerchants.length === 0 && (
                    <div className="p-8 text-center text-gray-500">
                      <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No merchants found</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Merchant Details */}
              <div className="bg-white rounded-xl border">
                <div className="p-4 border-b">
                  <h3 className="font-semibold text-gray-900">Merchant Details</h3>
                </div>
                
                {merchantDetails ? (
                  <div className="p-4 space-y-4">
                    <div>
                      <h4 className="font-semibold text-lg text-gray-900">{merchantDetails.merchant.business_name}</h4>
                      <StatusIndicator active={merchantDetails.merchant.is_active} />
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <MapPin className="w-4 h-4" />
                        {merchantDetails.merchant.street_address}, {merchantDetails.merchant.city}
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Phone className="w-4 h-4" />
                        {merchantDetails.merchant.phone}
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <DollarSign className="w-4 h-4" />
                        Plan: <span className="capitalize font-medium">{merchantDetails.merchant.plan}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Calendar className="w-4 h-4" />
                        Joined: {new Date(merchantDetails.merchant.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    
                    <div className="border-t pt-4">
                      <h5 className="font-medium text-gray-900 mb-2">Users ({merchantDetails.users.length})</h5>
                      <div className="space-y-2">
                        {merchantDetails.users.map(u => (
                          <div key={u.id} className="flex items-center gap-2 text-sm">
                            <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs">
                              {u.name?.charAt(0) || 'U'}
                            </div>
                            <span>{u.email}</span>
                            <span className="text-gray-400 text-xs">({u.role})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="border-t pt-4">
                      <h5 className="font-medium text-gray-900 mb-2">Recent Orders ({merchantDetails.recent_orders.length})</h5>
                      {merchantDetails.recent_orders.length > 0 ? (
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {merchantDetails.recent_orders.slice(0, 5).map(order => (
                            <div key={order.id} className="flex items-center justify-between text-sm">
                              <span className="font-mono text-gray-500">{order.id.slice(0, 8)}</span>
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                order.status === 'delivered' ? 'bg-emerald-100 text-emerald-700' :
                                order.status === 'in_transit' ? 'bg-indigo-100 text-indigo-700' :
                                'bg-amber-100 text-amber-700'
                              }`}>
                                {order.status}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No orders yet</p>
                      )}
                    </div>
                    
                    <div className="border-t pt-4">
                      <h5 className="font-medium text-gray-900 mb-2">API Usage</h5>
                      <p className="text-sm text-gray-600">{merchantDetails.api_logs.length} calls recorded</p>
                    </div>
                    
                    <div className="border-t pt-4">
                      <Button
                        variant={merchantDetails.merchant.is_active ? "destructive" : "default"}
                        className="w-full"
                        onClick={() => toggleMerchantStatus(merchantDetails.merchant.id, merchantDetails.merchant.is_active)}
                      >
                        {merchantDetails.merchant.is_active ? 'Deactivate Merchant' : 'Activate Merchant'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <Eye className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Select a merchant to view details</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
          
          {/* Users Tab */}
          <TabsContent value="users">
            <div className="bg-white rounded-xl border">
              <div className="p-4 border-b">
                <h3 className="font-semibold text-gray-900">All Users</h3>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Merchant</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredUsers.map(u => (
                      <tr key={u.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                              <span className="text-indigo-600 font-semibold text-sm">
                                {u.name?.charAt(0) || 'U'}
                              </span>
                            </div>
                            <span className="font-medium text-gray-900">{u.name || 'Unknown'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{u.email}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            u.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                            u.role === 'driver' ? 'bg-blue-100 text-blue-700' :
                            u.role === 'merchant_admin' ? 'bg-indigo-100 text-indigo-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {u.role}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {u.merchant_name || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {filteredUsers.length === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No users found</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
          
          {/* Drivers Tab */}
          <TabsContent value="drivers">
            <div className="bg-white rounded-xl border">
              <div className="p-3 sm:p-4 border-b flex flex-wrap items-center justify-between gap-3">
                <h3 className="font-semibold text-gray-900">All Drivers</h3>
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-amber-500" />
                    Pending: {drivers.filter(d => d.approval_status === 'pending').length}
                  </span>
                  <span className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-emerald-500" />
                    Available: {drivers.filter(d => d.available).length}
                  </span>
                  <span className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-gray-300" />
                    Busy: {drivers.filter(d => !d.available).length}
                  </span>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                {filteredDrivers
                  .slice()
                  .sort((a, b) => (a.approval_status === 'pending' ? -1 : 0) - (b.approval_status === 'pending' ? -1 : 0))
                  .map(driver => (
                  <div 
                    key={driver.id} 
                    className={`p-4 rounded-lg border ${
                      driver.approval_status === 'pending' ? 'border-amber-300 bg-amber-50' :
                      driver.approval_status === 'rejected' ? 'border-red-200 bg-red-50' :
                      driver.available ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200'
                    }`}
                    data-testid={`driver-card-${driver.id}`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center overflow-hidden ${
                        driver.available ? 'bg-emerald-500' : 'bg-gray-300'
                      }`}>
                        {driver.avatar_url ? (
                          <img
                            src={driver.avatar_url.startsWith('http') ? driver.avatar_url : `${process.env.REACT_APP_BACKEND_URL}${driver.avatar_url}`}
                            alt={driver.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Truck className="w-5 h-5 text-white" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-medium text-gray-900 truncate">{driver.name}</h4>
                        <p className="text-xs text-gray-500">{driver.city}</p>
                      </div>
                    </div>

                    <div className="mb-3">
                      <ApprovalBadge status={driver.approval_status} />
                    </div>
                    
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Phone className="w-3 h-3" />
                        {driver.phone}
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Mail className="w-3 h-3" />
                        <span className="truncate">{driver.email}</span>
                      </div>
                      {driver.vehicle_make_model && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Truck className="w-3 h-3" />
                          <span className="truncate">{driver.vehicle_make_model} · {driver.vehicle_colour}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-3 pt-3 border-t space-y-2">
                      <div className="flex items-center justify-between">
                        <span className={`text-xs font-medium ${
                          driver.available ? 'text-emerald-600' : 'text-gray-500'
                        }`}>
                          {driver.available ? 'Available' : (driver.approval_status === 'pending' ? 'Awaiting approval' : 'Busy')}
                        </span>
                        <button
                          onClick={() => setSelectedDriver(driver)}
                          className="text-xs text-indigo-600 hover:underline font-medium"
                          data-testid={`view-driver-${driver.id}`}
                        >
                          View details
                        </button>
                      </div>
                      {driver.approval_status === 'pending' && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => approveDriver(driver.id)}
                            className="flex-1 h-8 text-xs bg-emerald-600 hover:bg-emerald-700"
                            data-testid={`approve-driver-${driver.id}`}
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => rejectDriver(driver.id)}
                            className="flex-1 h-8 text-xs border-red-300 text-red-600 hover:bg-red-50"
                            data-testid={`reject-driver-${driver.id}`}
                          >
                            Reject
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                {filteredDrivers.length === 0 && (
                  <div className="col-span-3 p-8 text-center text-gray-500">
                    <Truck className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No drivers found</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Driver Details Modal */}
      {selectedDriver && (
        <DriverDetailsModal
          driver={selectedDriver}
          token={token}
          onClose={() => setSelectedDriver(null)}
          onApprove={approveDriver}
          onReject={rejectDriver}
        />
      )}
    </div>
  );
}
